const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_FLUSH_DELAY_MS = 1500;
const DEFAULT_STATS_SAMPLE_INTERVAL_MS = 60000;
const STATS_WORKER_TIMEOUT_MS = 750;
const DUPLICATE_WINDOW_MS = 1000;
const MAX_STRING_LENGTH = 180;

let statsWorker;
let nextStatsWorkerRequestId = 1;
const pendingStatsWorkerRequests = new Map();

const allowedStringFields = [
    'eventType',
    'sessionId',
    'streamId',
    'streamType',
    'division',
    'desk',
    'site',
    'cameraId',
    'cameraName',
    'kurentoId',
    'transactionId',
    'aiqInstanceId',
    'peerConnectionState',
    'iceConnectionState',
    'iceGatheringState',
    'signalingState',
    'dtlsState',
    'srtpState',
    'candidatePairState',
    'localCandidateType',
    'remoteCandidateType',
    'candidateProtocol',
    'networkType',
    'videoReadyState',
    'operatorImpact',
    'symptom',
    'outcome',
    'failureReason',
    'hardRefreshOutcome',
    'userAgentFamily',
];

const allowedNumberFields = [
    'timeToFirstFrameMs',
    'stallDurationMs',
    'frozenDurationMs',
    'blackFrameRatio',
    'framesDecoded',
    'framesDropped',
    'jitterMs',
    'rttMs',
    'packetsLost',
    'reconnectAttempt',
];

const allowedBooleanFields = ['playing', 'stalled', 'frozen', 'blackFrame', 'muted', 'visible', 'hardRefreshAttempted'];

export class WebRtcTelemetryService {
    #config;
    #queue = [];
    #flushTimeoutId;
    #lastEventBySignature = new Map();
    #lastStatsSnapshotByPeerConnection = new WeakMap();
    #sendFailureLogged = false;

    constructor(config = resolveWebRtcTelemetryConfig()) {
        this.#config = {
            statsSampleIntervalMs: DEFAULT_STATS_SAMPLE_INTERVAL_MS,
            runtimeEnabled: false,
            ...config,
        };
    }

    isEnabled() {
        return Boolean(this.#config.enabled && this.#config.runtimeEnabled && this.#config.endpoint && typeof fetch === 'function');
    }

    setRuntimeEnabled(enabled) {
        this.#config = {
            ...this.#config,
            runtimeEnabled: Boolean(enabled),
        };

        if (!this.#config.runtimeEnabled) {
            this.#queue = [];
            clearTimeout(this.#flushTimeoutId);
            this.#flushTimeoutId = undefined;
        }
    }

    record(event, options = {}) {
        if (!this.isEnabled()) return;

        const baseEvent = sanitizeOutboundEvent({
            timestamp: new Date().toISOString(),
            userAgentFamily: detectUserAgentFamily(),
            ...event,
        });

        if (!baseEvent) return;

        const { peerConnection, videoElement, immediate = false } = options;
        this.#buildEventWithSnapshots(baseEvent, peerConnection, videoElement)
            .then((snapshotEvent) => this.#enqueue(snapshotEvent, { immediate }))
            .catch(() => this.#enqueue(baseEvent, { immediate }));
    }

    recordPeerConnectionEvent(event, peerConnection, options = {}) {
        this.record(
            {
                ...event,
                ...peerConnectionState(peerConnection),
            },
            {
                ...options,
                peerConnection,
            }
        );
    }

    recordVideoEvent(event, videoElement, options = {}) {
        this.record(
            {
                ...event,
                ...videoElementState(videoElement),
            },
            {
                ...options,
                videoElement,
            }
        );
    }

    flush({ keepalive = false } = {}) {
        if (!this.#queue.length || !this.isEnabled()) return;

        clearTimeout(this.#flushTimeoutId);
        this.#flushTimeoutId = undefined;

        const batch = this.#queue.splice(0, DEFAULT_BATCH_SIZE);
        const body = JSON.stringify({ events: batch });

        fetch(this.#config.endpoint, {
            method: 'POST',
            credentials: 'omit',
            keepalive,
            headers: {
                'Content-Type': 'application/json',
            },
            body,
        }).catch((err) => this.#handleSendFailure(err));

        if (this.#queue.length) this.#scheduleFlush();
    }

    async #buildEventWithSnapshots(baseEvent, peerConnection, videoElement) {
        const includePeerConnectionStats = this.#shouldCollectPeerConnectionStats(peerConnection);
        const [pcSnapshot, videoSnapshot] = await Promise.all([
            collectPeerConnectionSnapshot(peerConnection, { includeStats: includePeerConnectionStats }),
            Promise.resolve(videoElementState(videoElement)),
        ]);

        return sanitizeOutboundEvent({
            ...baseEvent,
            ...pcSnapshot,
            ...videoSnapshot,
        });
    }

    #enqueue(event, { immediate = false } = {}) {
        const cleanEvent = sanitizeOutboundEvent(event);
        if (!cleanEvent || this.#isDuplicate(cleanEvent)) return;

        this.#queue.push(cleanEvent);

        if (immediate) {
            this.flush({ keepalive: true });
            return;
        }

        this.#scheduleFlush();
    }

    #scheduleFlush() {
        if (this.#flushTimeoutId) return;
        this.#flushTimeoutId = setTimeout(() => this.flush(), DEFAULT_FLUSH_DELAY_MS);
    }

    #shouldCollectPeerConnectionStats(peerConnection) {
        if (!peerConnection) return false;

        const sampleIntervalMs = Number(this.#config.statsSampleIntervalMs);
        if (!Number.isFinite(sampleIntervalMs) || sampleIntervalMs <= 0) return false;

        const now = Date.now();
        const lastAt = this.#lastStatsSnapshotByPeerConnection.get(peerConnection) || 0;
        if (now - lastAt < sampleIntervalMs) return false;

        this.#lastStatsSnapshotByPeerConnection.set(peerConnection, now);
        return true;
    }

    #isDuplicate(event) {
        const signature = [
            event.eventType,
            event.cameraId,
            event.kurentoId,
            event.streamType,
            event.peerConnectionState,
            event.iceConnectionState,
            event.iceGatheringState,
            event.signalingState,
            event.dtlsState,
            event.videoReadyState,
            event.playing,
            event.stalled,
            event.frozen,
            event.blackFrame,
            event.hardRefreshAttempted,
            event.hardRefreshOutcome,
            event.failureReason,
        ].join('|');
        const now = Date.now();
        const lastAt = this.#lastEventBySignature.get(signature) || 0;
        this.#lastEventBySignature.set(signature, now);
        return now - lastAt < DUPLICATE_WINDOW_MS;
    }

    #handleSendFailure(err) {
        if (import.meta.env.VITE_ENV === 'production' || this.#sendFailureLogged) return;
        this.#sendFailureLogged = true;
        console.warn(`[StreamingObservability] WebRTC telemetry delivery failed: ${err?.message || err}`);
    }
}

export function resolveWebRtcTelemetryConfig(env = import.meta.env) {
    const profile = cleanString(env.VITE_ENV) || 'local';
    const enabled = parseBoolean(env.VITE_STREAM_OBSERVABILITY_WEBRTC_TELEMETRY_ENABLED);
    const endpoint = cleanString(env.VITE_STREAM_OBSERVABILITY_WEBRTC_TELEMETRY_URL) || defaultTelemetryEndpoint(profile);
    const statsSampleIntervalMs = cleanNumber(env.VITE_STREAM_OBSERVABILITY_WEBRTC_STATS_SAMPLE_INTERVAL_MS) || DEFAULT_STATS_SAMPLE_INTERVAL_MS;

    return {
        enabled,
        endpoint,
        profile,
        runtimeEnabled: false,
        statsSampleIntervalMs,
    };
}

export function buildStreamTelemetryContext({ camConfig = {}, transactionInfo = {}, currentDesk = {}, videoOutputId, streamType, tabId } = {}) {
    return sanitizeOutboundEvent({
        streamId: videoOutputId,
        streamType: streamType || camConfig.videoData?.streamType,
        desk: currentDesk.name || transactionInfo.deskName,
        site: transactionInfo.locationName || camConfig.locationName,
        cameraId: camConfig.camId,
        cameraName: camConfig.generatedName || camConfig.name,
        kurentoId: camConfig.kurentoId,
        transactionId: transactionInfo.id || tabId,
    });
}

export async function collectPeerConnectionSnapshot(peerConnection, { includeStats = true } = {}) {
    if (!peerConnection) return {};

    const snapshot = peerConnectionState(peerConnection);
    if (!includeStats || typeof peerConnection.getStats !== 'function') return snapshot;
    if (typeof Worker !== 'function') return snapshot;

    const report = await peerConnection.getStats();
    const statsSnapshot = await summarizeStatsReportInWorker(report);

    return sanitizeOutboundEvent({
        ...snapshot,
        ...statsSnapshot,
    });
}

export function summarizePeerConnectionStats(report) {
    const stats = Array.from(report?.values?.() || report || []);
    return summarizePeerConnectionStatsItems(stats);
}

export function summarizePeerConnectionStatsItems(stats = []) {
    const selectedPair = findSelectedCandidatePair(stats);
    const inboundVideo = findInboundVideoStats(stats);
    const transport = findTransportStats(stats);

    return sanitizeOutboundEvent({
        ...candidatePairState(stats, selectedPair),
        ...inboundVideoState(inboundVideo, selectedPair),
        dtlsState: transport?.dtlsState,
        srtpState: transport?.srtpCipher ? 'negotiated' : undefined,
    });
}

export function sanitizeOutboundEvent(event = {}) {
    const sanitized = {};

    for (const field of allowedStringFields) {
        const value = cleanString(event[field]);
        if (value !== null) sanitized[field] = value;
    }

    for (const field of allowedNumberFields) {
        const value = cleanNumber(event[field]);
        if (value !== null) sanitized[field] = value;
    }

    for (const field of allowedBooleanFields) {
        const value = cleanBoolean(event[field]);
        if (value !== null) sanitized[field] = value;
    }

    const timestamp = validTimestamp(event.timestamp);
    if (timestamp) sanitized.timestamp = timestamp;

    return Object.keys(sanitized).length ? sanitized : null;
}

function peerConnectionState(peerConnection) {
    if (!peerConnection) return {};
    return {
        peerConnectionState: peerConnection.connectionState,
        iceConnectionState: peerConnection.iceConnectionState,
        iceGatheringState: peerConnection.iceGatheringState,
        signalingState: peerConnection.signalingState,
    };
}

function videoElementState(videoElement) {
    if (!videoElement) return {};

    const playing = Boolean(!videoElement.paused && !videoElement.ended && videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA);

    return {
        playing,
        muted: videoElement.muted,
        visible: document.visibilityState === 'visible',
        videoReadyState: readyStateLabel(videoElement.readyState),
    };
}

function findSelectedCandidatePair(stats) {
    const pairs = stats.filter((item) => item?.type === 'candidate-pair');

    return (
        pairs.find((item) => item.selected) ||
        pairs.find((item) => item.nominated && item.state === 'succeeded') ||
        pairs.find((item) => item.state === 'succeeded') ||
        null
    );
}

function findInboundVideoStats(stats) {
    for (const item of stats) {
        if (item?.type === 'inbound-rtp' && (item.kind === 'video' || item.mediaType === 'video')) return item;
    }
    return null;
}

function findTransportStats(stats) {
    for (const item of stats) {
        if (item?.type === 'transport') return item;
    }
    return null;
}

function candidatePairState(stats, selectedPair) {
    if (!selectedPair) return {};

    const statsById = new Map(stats.filter((item) => item?.id).map((item) => [item.id, item]));
    const localCandidate = selectedPair.localCandidateId ? statsById.get(selectedPair.localCandidateId) : null;
    const remoteCandidate = selectedPair.remoteCandidateId ? statsById.get(selectedPair.remoteCandidateId) : null;

    return {
        candidatePairState: selectedPair.state,
        localCandidateType: localCandidate?.candidateType,
        remoteCandidateType: remoteCandidate?.candidateType,
        candidateProtocol: localCandidate?.protocol || remoteCandidate?.protocol,
        networkType: localCandidate?.networkType,
    };
}

function inboundVideoState(inboundVideo, selectedPair) {
    return {
        framesDecoded: inboundVideo?.framesDecoded,
        framesDropped: inboundVideo?.framesDropped,
        jitterMs: secondsToMillis(inboundVideo?.jitter),
        rttMs: secondsToMillis(selectedPair?.currentRoundTripTime),
        packetsLost: inboundVideo?.packetsLost,
    };
}

function readyStateLabel(value) {
    switch (value) {
        case HTMLMediaElement.HAVE_NOTHING:
            return 'HAVE_NOTHING';
        case HTMLMediaElement.HAVE_METADATA:
            return 'HAVE_METADATA';
        case HTMLMediaElement.HAVE_CURRENT_DATA:
            return 'HAVE_CURRENT_DATA';
        case HTMLMediaElement.HAVE_FUTURE_DATA:
            return 'HAVE_FUTURE_DATA';
        case HTMLMediaElement.HAVE_ENOUGH_DATA:
            return 'HAVE_ENOUGH_DATA';
        default:
            return undefined;
    }
}

function secondsToMillis(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number * 1000 : undefined;
}

function parseBoolean(value) {
    return ['true', '1', 'yes', 'enabled'].includes(
        String(value || '')
            .trim()
            .toLowerCase()
    );
}

function defaultTelemetryEndpoint(profile) {
    switch (profile) {
        case 'develop':
            return 'https://streaming-observability-develop.birdseyesecurity.com/api/telemetry/webrtc';
        case 'staging':
            return 'https://streaming-observability-staging.birdseyesecurity.com/api/telemetry/webrtc';
        case 'production':
            return 'https://streaming-observability.birdseyesecurity.com/api/telemetry/webrtc';
        default:
            return '';
    }
}

function detectUserAgentFamily() {
    if (typeof navigator === 'undefined') return undefined;
    const ua = navigator.userAgent || '';
    if (/Edg\//.test(ua)) return 'Edge';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua)) return 'Safari';
    return 'Other';
}

function validTimestamp(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function cleanString(value) {
    if (value === undefined || value === null || value === '') return null;
    return String(value)
        .replace(/(rtsp:\/\/)([^:@/\s]+):([^@/\s]+)@/gi, '$1[redacted]:[redacted]@')
        .replace(/([?&](token|signature|X-Amz-Signature|password|access_token)=)[^&\s]+/gi, '$1[redacted]')
        .replace(/(authorization|cookie):\s*[^,\n]+/gi, '$1: [redacted]')
        .replace(/\brtsp:\/\/[^\s,]+/gi, 'rtsp://[redacted]')
        .replace(/\b(https?|wss?):\/\/[^\s,]+/gi, '$1://[redacted]')
        .slice(0, MAX_STRING_LENGTH);
}

function cleanNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return Math.round(number * 1000) / 1000;
}

function cleanBoolean(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (String(value).toLowerCase() === 'true') return true;
    if (String(value).toLowerCase() === 'false') return false;
    return null;
}

function summarizeStatsReportInWorker(report) {
    const worker = getStatsWorker();
    if (!worker) return Promise.resolve({});

    const requestId = nextStatsWorkerRequestId++;
    const stats = Array.from(report.values());

    return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
            pendingStatsWorkerRequests.delete(requestId);
            resolve({});
        }, STATS_WORKER_TIMEOUT_MS);

        pendingStatsWorkerRequests.set(requestId, {
            resolve: (result) => {
                clearTimeout(timeoutId);
                resolve(result || {});
            },
        });

        try {
            worker.postMessage({ type: 'summarize', requestId, stats });
        } catch {
            clearTimeout(timeoutId);
            pendingStatsWorkerRequests.delete(requestId);
            resolve({});
        }
    });
}

function getStatsWorker() {
    if (typeof Worker !== 'function') return null;
    if (statsWorker !== undefined) return statsWorker;

    try {
        statsWorker = new Worker(new URL('./webRtcTelemetryStatsWorker.js', import.meta.url), { type: 'module' });
        statsWorker.onmessage = (ev) => {
            const msg = ev.data;
            if (!msg || msg.type !== 'summary') return;
            const pending = pendingStatsWorkerRequests.get(msg.requestId);
            if (!pending) return;
            pendingStatsWorkerRequests.delete(msg.requestId);
            pending.resolve(msg.result);
        };
        statsWorker.onerror = () => {
            for (const [requestId, pending] of pendingStatsWorkerRequests.entries()) {
                pendingStatsWorkerRequests.delete(requestId);
                pending.resolve({});
            }
            statsWorker?.terminate?.();
            statsWorker = null;
        };
    } catch {
        statsWorker = null;
    }

    return statsWorker;
}

export const webRtcTelemetry = new WebRtcTelemetryService();
